import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { HERO_SLIDES, STATS, WA, scrollToId } from "@/lib/site";

export default function Hero() {
  const [zip, setZip] = useState("");
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 5500);
    return () => clearInterval(t);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    const z = zip.trim();
    window.open(WA(z ? `My zip code: ${z}` : ""), "_blank");
  };

  return (
    <section className="relative flex min-h-[100svh] items-center overflow-hidden bg-navy" id="top">
      {HERO_SLIDES.map((s, i) => (
        <div key={s.img} className="absolute inset-0 transition-opacity duration-[1200ms]" style={{ opacity: slide === i ? 1 : 0 }}>
          <img src={s.img} alt={s.alt} className="h-full w-full object-cover" />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-black/35" />

      <div className="relative z-10 mx-auto w-full max-w-[1200px] px-5 pb-24 pt-32 md:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="inline-flex items-center gap-2.5 border border-white/40 bg-black/25 px-4 py-2 text-[11px] uppercase tracking-[0.24em] text-white"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#e8a33d]" />
              Now booking — Central Florida
            </motion.div>

            <h1 className="mt-6 font-display text-[11.5vw] font-light leading-[1.06] text-white sm:text-5xl md:text-6xl xl:text-[4.3rem]">
              {["Central Florida's", "premier painting"].map((l, i) => (
                <span key={l} className="block overflow-hidden">
                  <motion.span
                    className="block"
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.76, 0, 0.24, 1] }}
                  >
                    {l}
                  </motion.span>
                </span>
              ))}
              <span className="block overflow-hidden">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, delay: 0.41, ease: [0.76, 0, 0.24, 1] }}
                >
                  <em className="italic text-[#e8a33d]">experts</em> for over 20+ years.
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-6 max-w-xl text-[15px] leading-relaxed text-white/85 md:text-base"
            >
              From sun-drenched exteriors to flawless luxury interiors — we protect and beautify
              your home with unmatched local expertise. Fully licensed and insured.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.75 }}
              className="mt-9 flex flex-col gap-3 sm:flex-row"
            >
              <button data-testid="hero-cta-primary" onClick={() => scrollToId("#contact")} className="btn-amber">
                Get Your Free Quote
                <span aria-hidden="true">→</span>
              </button>
              <button data-testid="hero-view-work-btn" onClick={() => scrollToId("#gallery")} className="btn-outline-w">
                View Our Work
              </button>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="lg:col-span-4 lg:col-start-9"
          >
            <div className="glass-card p-7 md:p-8">
              <p className="eyebrow !text-[#e8a33d]">Instant Estimate</p>
              <h2 className="mt-3 font-display text-2xl font-light leading-snug text-white">
                Enter your zip — we'll handle the rest.
              </h2>
              <form onSubmit={submit} className="mt-6 space-y-3">
                <input
                  data-testid="hero-zip-input"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  inputMode="numeric"
                  placeholder="ZIP code (e.g. 32801)"
                  className="input-zip"
                />
                <button data-testid="hero-estimate-submit" type="submit" className="btn-amber w-full">
                  Get Free Estimate
                  <span aria-hidden="true">→</span>
                </button>
              </form>
              <p className="mt-4 text-xs text-white/60">No pressure. No spam. Just a transparent quote.</p>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-7 left-1/2 z-10 flex -translate-x-1/2 gap-2.5">
        {HERO_SLIDES.map((s, i) => (
          <button
            key={s.img}
            data-testid={`hero-slide-dot-${i}`}
            aria-label={`Slide ${i + 1}`}
            onClick={() => setSlide(i)}
            className={`slide-dot ${slide === i ? "active" : ""}`}
          />
        ))}
      </div>
    </section>
  );
}

export function StatsBand() {
  return (
    <section className="border-b border-[#e6e6e1] bg-white">
      <div className="mx-auto grid max-w-[1200px] grid-cols-2 gap-8 px-5 py-10 md:grid-cols-4 md:px-8">
        {STATS.map((s) => (
          <div key={s.k}>
            <p className="font-display text-3xl font-medium text-navy md:text-4xl">{s.k}</p>
            <p className="mt-1 text-[11px] uppercase tracking-[0.14em] text-[#8a97a5]">{s.v}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
